import React, { useEffect, useRef, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";
import MenuList from "../MenuList.jsx";
import { excludedKeys } from "../../config/constant.js";
import { fetchApplicationImg } from "../../api/userService.js";
import SideBarContent from "./SideBarContent.jsx";
import DetailSection from "./DetailSection.jsx";

const CompanyAnalysisLayout = () => {
  const mapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const applicationData = useSelector(
    (state) => state.intakeData.selectedApplicationIdData
  );
  const company_details = applicationData?.analysis?.company_details;
  const uniqueValues = useSelector((state) => state.intakeData.uniqueValues);
  const selectedApplicationIdInfo = useMemo(() => {
    return applicationData?.analysis;
  }, [applicationData]);
  const company = applicationData?.company_name;

  useEffect(() => {
    if (applicationData) {
      setIsLoading(false);
    }
  }, [applicationData, isLoading]);

  useEffect(() => {
    const latitudes =
      selectedApplicationIdInfo?.company_details?.latitudes || [];
    const longitudes =
      selectedApplicationIdInfo?.company_details?.longitudes || [];

    // Fallback to default location if no coordinates are available
    const defaultPosition = { lat: 37.7749, lng: -122.4194 };
    let mapCenter = defaultPosition;

    // Ensure we have valid lat/lng pairs
    if (latitudes.length > 0 && longitudes.length > 0) {
      mapCenter = {
        lat: Number(latitudes[0]), // Set initial center to first location
        lng: Number(longitudes[0]),
      };
    }

    const loadGoogleMapsScript = () => {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          resolve();
        } else {
          const script = document.createElement("script");
          script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAibBe9XsJ7hHfBuiihzcoSlbGp6P7IB8I&v=weekly`;
          script.defer = true;
          script.onload = resolve;
          script.onerror = () =>
            reject(new Error("Failed to load Google Maps script."));
          document.head.appendChild(script);
        }
      });
    };

    const initMap = async () => {
      if (!mapRef.current) {
        console.error("Map container not found!");
        return;
      }

      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: mapCenter, // Set map center
        zoom: 12,
        mapId: "15431d2b469f209e",
      });

      // Add multiple markers based on latitudes & longitudes
      if (latitudes.length > 0 && latitudes.length === longitudes.length) {
        latitudes.forEach((lat, index) => {
          const lng = longitudes[index];
          if (!lat || !lng) return;

          const marker = new window.google.maps.Marker({
            position: { lat: Number(lat), lng: Number(lng) },
            map: mapInstance,
            title: `Location ${index + 1}`,
          });

          const streetViewService = new window.google.maps.StreetViewService();

          marker.addListener("click", () => {
            const latLng = new window.google.maps.LatLng(
              Number(lat),
              Number(lng)
            );

            streetViewService.getPanorama(
              { location: latLng, radius: 50 },
              (data, status) => {
                if (status === window.google.maps.StreetViewStatus.OK) {
                  const streetViewUrl = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
                  window.open(streetViewUrl, "_blank");
                } else {
                  const satelliteUrl = `https://www.google.com/maps/@${lat},${lng},20z/data=!3m1!1e3`;
                  window.open(satelliteUrl, "_blank");
                }
              }
            );
          });
        });
      }

      // Apply 3D view with initial tilt and heading
      window.google.maps.event.addListenerOnce(
        mapInstance,
        "tilesloaded",
        () => {
          mapInstance.setTilt(45);
          mapInstance.setHeading(20);
        }
      );

      // Initialize InfoWindow
      infoWindowRef.current = new window.google.maps.InfoWindow();
    };

    loadGoogleMapsScript()
      .then(initMap)
      .catch((error) =>
        console.error("Failed to load Google Maps script:", error)
      );
  }, [selectedApplicationIdInfo]);

  const getWebsiteUrl = (url) => {
    const website = url;
    if (!website) return ""; // Return empty string if website is undefined or null
    if (website.startsWith("https://") || website.startsWith("http://")) {
      return website;
    }
    return `https://${website}`;
  };

  const fetchImgData = async (urlLink) => {
    const url = getWebsiteUrl(urlLink);
    if (url) {
      const image = await fetchApplicationImg(url);
      if ("logo_url" in image) {
        setLogoUrl(image.logo_url);
      }
    }
  };

  useEffect(() => {
    fetchImgData(company_details?.website);
  }, [applicationData]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const sections = selectedApplicationIdInfo
    ? Object.keys(selectedApplicationIdInfo).filter(
        (key) => !excludedKeys.includes(key)
      )
    : [];

  return (
    <div className="bg-gray-100 text-gray-800 ">
      <div className="flex items-center space-x-4 my-2">
        <MenuList isOpen={isMenuOpen} toggleMenu={toggleMenu} />
        <h1 className="text-2xl text-orange-600 font-semibold">
          Company Analysis
        </h1>
      </div>
      <div className="relative flex flex-col lg:flex-row gap-4">
        {applicationData === null && isLoading && (
          <div className="absolute inset-0 bg-black bg-opacity-40 z-10 flex flex-col items-center justify-center space-y-4">
            <CircularProgress size={60} sx={{ color: "#ea580c" }} />
            <p className="text-base font-bold">
              Fetching real-time data…
            </p>
          </div>
        )}
        {applicationData !== null && (
          <>
            {/* Sidebar */}
            <SideBarContent
              company={company}
              fetchImgData={fetchImgData}
              uniqueValues={uniqueValues}
              setIsLoading={setIsLoading}
              setLogoUrl={setLogoUrl}
              logoUrl={logoUrl}
              applicationData={applicationData}
              getWebsiteUrl={getWebsiteUrl}
              mapRef={mapRef}
            />

            {/* Main Content */}
            <DetailSection
              sections={sections}
              selectedApplicationIdInfo={selectedApplicationIdInfo}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CompanyAnalysisLayout;
